export type IndonesiaTimezone = "WIB" | "WITA" | "WIT";

export interface CreateEventDateInput {
  date: string;
  time: string;
  timezone: IndonesiaTimezone;
}

export interface EventDate {
  iso: string;
  date: string;
  time: string;
  timezone: IndonesiaTimezone;
}

const INDONESIA_TIMEZONE_OFFSETS: Record<IndonesiaTimezone, number> = {
  WIB: 7,
  WITA: 8,
  WIT: 9,
};

const EVENT_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const EVENT_TIME_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

function splitDate(value: string) {
  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    throw new Error("[createEventDate] date must contain valid year, month, and day");
  }

  return { year, month, day };
}

function splitTime(value: string) {
  const [hour, minute] = value.split(":").map(Number);

  if (hour === undefined || minute === undefined) {
    throw new Error("[createEventDate] time must contain valid hour and minute");
  }

  return { hour, minute };
}

function assertValidDate(value: string) {
  if (!EVENT_DATE_REGEX.test(value)) {
    throw new Error("[createEventDate] date must use YYYY-MM-DD format");
  }

  const { year, month, day } = splitDate(value);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    throw new Error("[createEventDate] date must be a valid calendar date");
  }
}

function assertValidTime(value: string) {
  if (!EVENT_TIME_REGEX.test(value)) {
    throw new Error("[createEventDate] time must use HH:mm 24-hour format");
  }
}

export function createEventDate(input: CreateEventDateInput): EventDate {
  assertValidDate(input.date);
  assertValidTime(input.time);

  const { year, month, day } = splitDate(input.date);
  const { hour, minute } = splitTime(input.time);

  const timezoneOffset = INDONESIA_TIMEZONE_OFFSETS[input.timezone];
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour - timezoneOffset, minute));

  return {
    iso: utcDate.toISOString(),
    date: input.date,
    time: input.time,
    timezone: input.timezone,
  };
}
